// steps/tabs/meter-readings/subtabs/ActualReadingsSubTab.tsx
import { useMeterReadings } from "@features/bx/BulkBillGeneration/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { CheckCircle2 } from "lucide-react";
import React, { useState } from "react";
import { Badge } from "@shared/ui/badge";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import { useParams } from "react-router-dom";
import { MeterReadingFilters } from "./Filters/AllReadingFilters";

interface ActualReadingsSubTabProps {
  data?: any;
}

interface MeterReadingFiltersType {
  veeStatus: string;
  status: string;
  meterType: string;
  service: string;
  page?: number;
  limit?: number;
  readingType?: string;
}

const ActualReadingsSubTab: React.FC<ActualReadingsSubTabProps> = ({
  data,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [isListExpanded, setIsListExpanded] = useState(true);
  const pageSize = PAGE_SIZE;
  const { id, cycleId, action } = useParams();

  const [filters, setFilters] = useState<MeterReadingFiltersType>({
    veeStatus: "all",
    status: "all",
    meterType: "all",
    service: "all",
    page: currentPage,
    limit: pageSize,
    readingType: "actual",
  });

  const formatFiltersForHook = (
    filters: MeterReadingFiltersType,
  ): { page: number; limit: number; search?: string; [key: string]: any } => {
    const hookFilters: {
      page: number;
      limit: number;
      search?: string;
      [key: string]: any;
    } = {
      page: currentPage,
      limit: PAGE_SIZE,
    };

    // Add search term
    if (searchTerm.trim()) {
      hookFilters.search = searchTerm;
    }

    // Add VEE status filter
    if (filters.veeStatus && filters.veeStatus !== "all") {
      hookFilters.veeStatus = filters.veeStatus;
    }

    // Add status filter
    if (filters.status && filters.status !== "all") {
      hookFilters.status = filters.status;
    }

    // Add meter type filter
    if (filters.meterType && filters.meterType !== "all") {
      hookFilters.meterType = filters.meterType;
    }

    // Add service filter
    if (filters.service && filters.service !== "all") {
      hookFilters.service = filters.service;
    }

    if (filters.readingType) {
      hookFilters.readingType = filters.readingType;
    }

    return hookFilters;
  };

  const { data: meterReadings, isLoading } = useMeterReadings(cycleId as string, id as string, formatFiltersForHook(filters));

  // Define columns
  const columns: TableColumn<any>[] = [
    {
      key: "meterNo",
      header: "Resource ID",
    },
    {
      key: "consumerNumber",
      header: "Consumer",
      render: (value: string) => {
        return <span className="font-medium">{value || "N/A"}</span>;
      },
    },
    {
      key: "meterType",
      header: "Resource Type",
    },
    {
      key: "service",
      header: "Service",
    },
    {
      key: "lastReading",
      header: "Last Reading",
    },
    {
      key: "currentReading",
      header: "Current Reading",
    },
    {
      key: "consumption",
      header: "Consumption",
    },
    {
      key: "veeStatus",
      header: "VEE Status",
      type: "badge",
      sortable: false,
      width: "w-28",
      badgeConfig: {
        getVariant: (value: string) => {
          switch (value) {
            case "Pass":
              return "default";
            case "Adjusted":
              return "secondary";
            default:
              return "outline";
          }
        },
        getClassName: (value: string) => {
          switch (value) {
            case "Pass":
              return "bg-green-100 text-green-700";
            case "Adjusted":
              return "bg-yellow-100 text-yellow-700";
            default:
              return "";
          }
        },
      },
    },
    {
      key: "status",
      header: "Status",
      render: (value: any) => (
        <Badge className={getStatusBadgeClass(value)}>
          {value || "NA"}
        </Badge>
      )
    },
  ];

  const handleSearch = (searchValue: string, paramName: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFiltersChange = (newFilters: MeterReadingFiltersType) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      veeStatus: "all",
      status: "all",
      meterType: "all",
      service: "all",
    });
    setSearchTerm("");
    setCurrentPage(1);
  };

  // Calculate pagination
  const totalItems = meterReadings?.count || 0;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="space-y-4">
      {/* Info Banner - Green for Actual */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
        <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm text-green-900">
            Consumers with <strong>actual and adjusted readings</strong> will
            receive an <strong>actual bill</strong> based on their verified
            meter consumption.
          </p>
        </div>
      </div>

      {/* Meter Reading List */}
      <div className="bg-white rounded-lg border border-gray-200">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between cursor-pointer hover:bg-gray-50">
          <div>
            <h3 className="font-semibold text-base">Actual Meter Reading List</h3>
            <p className="text-sm text-gray-600">
              All actual meters in the current billing cycle
            </p>
          </div>
        </div>

        {/* Content */}
        {isListExpanded && (
          <div className="p-4">
            <GenericTable
              data={meterReadings?.results || []}
              columns={columns}
              rowKey="meterNo"
              loading={isLoading}
              searchPlaceholder="Search by meter ID, account, or consumer name"
              searchParamName="search"
              searchDebounceMs={300}
              onSearch={handleSearch}
              filterPanel={
                <MeterReadingFilters
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  onApplyFilters={handleFiltersChange}
                  onClearFilters={clearFilters}
                />
              }
              pagination={{
                currentPage,
                totalPages,
                totalItems,
                itemsPerPage: pageSize,
                startItem,
                endItem,
              }}
              onPageChange={handlePageChange}
              emptyMessage="No actual meter readings found"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ActualReadingsSubTab;
